/* eslint-disable @typescript-eslint/no-unsafe-call */
import { takeLatest, put, race, take, delay } from "redux-saga/effects";
import { call, select } from "typed-redux-saga";
import * as Sentry from "@sentry/react-native";
import Constants from "expo-constants";
import {
  CREATE_TPOS_ORDER,
  PRINT_RECEIPT_ASYNC,
} from "../constants/actionTypesTpos";
import { APIError, PrinterError } from "../types/error-types";
import { Order, OrdersFetchAction, OrderType } from "../types/order";
import { isUserAuthenticated, getUserId, getBranchId } from "../selectors/user";
import url from "../api";
import {
  clearError,
  doAddOrders,
  doCreateOrderDone,
  doCreateOrderStart,
  doAsyncOrderCompleted,
  doAsyncOrderFailed,
  incrementOrderCurrentPage,
  printReceiptAsync,
  saveError,
} from "../actions";
import { AggregatorOrder, TposOrder } from "../types/tposOrder";
import { makeApiCall } from "./general";
import { clearBasketAfterOrder } from "../actions/checkout";
import { lineItemProducts } from "../selectors/lineItem";
import { getSelectedOptionsForOrder } from "../selectors/options";
import { PrintReceiptAsyncAction } from "../types/printer";
import { getPrinterHost, getPrinterUrl } from "../selectors/printer";
import axios, { AxiosError } from "axios";
import {
  ASYNC_ORDER_FAILED,
  ORDERS_FETCH,
  RESET_ORDER_CURRENT_PAGE,
  START_ASYNC_ORDER_FETCH,
} from "../constants/actionTypes";
import { getAllOrders, getPastOrdersPaginationCount } from "../selectors/order";
import { doStartAsyncOrderFetch } from "../actions";
import { getConfig, getEnableAsyncFlowForTpos } from "@/app/selectors/config";
import { doSetConfig } from "../actions/config";
import { triggerVersionCheck } from "@/app/utils/versionCheck";

export default function* orderSaga(): Generator {
  yield takeLatest(ORDERS_FETCH, handleFetchOrders);
  yield takeLatest(CREATE_TPOS_ORDER, handleTPOSOrder);
  yield takeLatest(START_ASYNC_ORDER_FETCH, handleAsyncOrderPolling);
  yield takeLatest(PRINT_RECEIPT_ASYNC, handlePrintReceipt);
}

export function* handleFetchOrders({
  payload: isPaginatedLoad,
}: OrdersFetchAction): Generator {
  if (!isPaginatedLoad) {
    yield put({ type: RESET_ORDER_CURRENT_PAGE });
  }

  const userAuthToken = yield* select(isUserAuthenticated);
  const page = yield* select(getPastOrdersPaginationCount);
  const allOrders: OrderType[] = yield* select(getAllOrders) as any;
  if (userAuthToken) {
    const { listOrders } = url;
    const data: OrderType[] & APIError = yield* call(
      makeApiCall,
      listOrders(page),
      undefined,
      {
        headers: {
          "ALBAIK-Auth-Token": userAuthToken,
        },
      },
    );
    if (data && !data.error && !data.errors && data.length > 0) {
      if (isPaginatedLoad && page !== 1) {
        yield put(doAddOrders([...allOrders, ...data]));
      } else {
        yield put(doAddOrders(data));
      }
      yield put(incrementOrderCurrentPage());
    }
  } else {
    yield put(doAddOrders([]));
  }
}

function* getTPOSOrderParams() {
  const items = yield* select(getSelectedOptionsForOrder);
  const { order } = url;

  const orderParams = {
    order: {
      items,
    },
  };

  return {
    orderParams,
    order,
  };
}

function* fetchFeatureFlags(userAuthToken: string | null): Generator {
  try {
    const { featureFlags } = url;
    const featureFlagsData = yield* call(makeApiCall, featureFlags, null, {
      headers: { "ALBAIK-Auth-Token": userAuthToken },
    });

    const currentConfig = yield* select(getConfig);
    yield put(
      doSetConfig({
        ...currentConfig,
        featureFlags: {
          ...currentConfig.featureFlags,
          ...featureFlagsData,
        },
      }),
    );
  } catch (err: unknown) {
    Sentry.captureException(err);
  }
}

export function* handleTPOSOrder(): Generator {
  const userAuthToken = yield* select(isUserAuthenticated);
  const items = yield* select(lineItemProducts);

  if (!items || items.length === 0) {
    return;
  }

  const tposParams = yield* call(getTPOSOrderParams);
  if (!tposParams) return;

  yield put(doCreateOrderStart());
  yield put(clearError());

  const { orderParams, order } = tposParams;
  const data: (TposOrder | AggregatorOrder) & APIError = yield* call(
    makeApiCall,
    order,
    orderParams,
    {
      headers: {
        "ALBAIK-Auth-Token": userAuthToken,
        "Request-Origin-App": "tpos-web",
        "Build-SHA": Constants.expoConfig?.extra?.gitSha || "development",
      },
    },
  ) as any;
  if (data?.error || data?.errors) {
    yield put(saveError(data));
    yield put(doCreateOrderDone());
  } else {
    yield* call(fetchFeatureFlags, userAuthToken);
    const asyncEnabled = yield* select(getEnableAsyncFlowForTpos);
    if (asyncEnabled) {
      yield put(doStartAsyncOrderFetch((data as Order).id));
    } else {
      yield put(doCreateOrderDone(data as OrderType));
      yield put(printReceiptAsync(data as Order));
      yield put(clearBasketAfterOrder());
    }
  }
}

export function* handleAsyncOrderPolling(action: {
  type: string;
  payload: number;
}): Generator {
  const orderId = action.payload;
  const userId = yield* select(getUserId);
  const branchId = yield* select(getBranchId);

  const sentryContext = {
    orderId,
    userId,
    branchId,
  };

  try {
    const { result, timeout, cancelled } = yield race({
      result: call(pollOrderStatus, orderId),
      timeout: delay(POLL_TIMEOUT_MS),
      cancelled: take(ASYNC_ORDER_FAILED),
    });

    if (cancelled) {
      Sentry.captureMessage(
        "Async order placement to Albaik client failed - cancelled",
        {
          level: "error",
          tags: { flow: "async-order-polling", reason: "cancelled" },
          extra: sentryContext,
        },
      );
      yield put(doAsyncOrderFailed());
      return;
    }

    if (timeout) {
      Sentry.captureMessage(
        "Async order placement to Albaik client failed - timeout",
        {
          level: "error",
          tags: { flow: "async-order-polling", reason: "timeout" },
          extra: {
            ...sentryContext,
            timeoutMs: POLL_TIMEOUT_MS,
          },
        },
      );
      yield put(
        saveError({
          errors: [
            "Order processing is taking longer than expected. Please check your order history.",
          ],
        }),
      );
      yield put(doAsyncOrderFailed());
      return;
    }

    if (result?.success) {
      if (result.order) {
        yield put(doCreateOrderDone(result.order));
        yield put(printReceiptAsync(result.order as Order));
        yield put(clearBasketAfterOrder());
      }
      yield put(doAsyncOrderCompleted());
      yield call(triggerVersionCheck);
    } else {
      Sentry.captureMessage(
        "Async order placement to Albaik client failed - order rejected or error",
        {
          level: "error",
          tags: { flow: "async-order-polling", reason: "order-failed" },
          extra: {
            ...sentryContext,
            errorMessage: result?.error || "unknown error",
          },
        },
      );
      yield put(
        saveError({
          errors: [result?.error || "Order processing failed."],
        }),
      );
      yield put(doAsyncOrderFailed());
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { flow: "async-order-polling", reason: "exception" },
      extra: sentryContext,
    });
    yield put(
      saveError({
        errors: [
          "Error checking order status. Please check your order history.",
        ],
      }),
    );
    yield put(doAsyncOrderFailed());
  }
}

const MAX_POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 65000;
const MAX_POLL_ATTEMPTS = 15;
const POLL_BASE_DELAY_MS = 1000;

function* pollOrderStatus(orderId: number): Generator {
  const userAuthToken = yield* select(isUserAuthenticated);

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    const { getOrder } = url;
    const order = yield* call(makeApiCall, getOrder(orderId), undefined, {
      headers: { "ALBAIK-Auth-Token": userAuthToken },
    });

    if (order?.status === "Accepted" && order?.albaikIdentifier != null) {
      return { success: true, order: order };
    }

    if (order?.status === "Rejected") {
      return { success: false, error: "Order was rejected. Please try again." };
    }

    const interval = Math.min(
      (attempt + 1) * POLL_BASE_DELAY_MS,
      MAX_POLL_INTERVAL_MS,
    );
    yield delay(interval);
  }

  return {
    success: false,
    error: "Order processing timeout. Please check your order history.",
  };
}

export function* handlePrintReceipt({
  payload: order,
}: PrintReceiptAsyncAction): Generator {
  try {
    const printerHost = yield* select(getPrinterHost);
    const printerUrl = yield* select(getPrinterUrl);

    if (!printerHost || !printerUrl) {
      yield put(
        saveError({
          message: "Printer Error",
          errors: ["Printer configuration is missing"],
        }),
      );
      return;
    }

    if (!order?.albaikIdentifier || !order?.backupPin || !order?.url) {
      yield put(
        saveError({
          message: "Printer Error",
          errors: ["Insufficient Order details for printing"],
        }),
      );
      return;
    }

    const printerAPIUrl = `${printerHost}/Ticket?printerUrl=${printerUrl}`;
    yield* call(
      axios.post,
      printerAPIUrl,
      {
        orderId: order.albaikIdentifier.toString(),
        orderPin: order.backupPin,
        collectionUrl: order.url,
        lane: order.lane?.name,
        price: order.total,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  } catch (err: unknown) {
    const error = err as AxiosError<PrinterError>;
    let errorMessage = error.message;

    if (error.response?.data) {
      const printerError = error.response.data;

      if (printerError.errors && typeof printerError.errors === "object") {
        const validationErrors = Object.entries(printerError.errors)
          .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
          .join("; ");

        errorMessage = `${printerError.title}: ${validationErrors}`;
      } else if (printerError.title) {
        errorMessage = printerError.title;
      }
    }
    yield put(
      saveError({
        message: "Printer Error",
        errors: [errorMessage],
      }),
    );
  }
}
